import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Plus, X, Upload } from 'lucide-react';
import { API_BASE } from '../config';
import { hasEditAccess } from '../permissionConfig';
import { notifyEditBlocked } from '../components/EditAccessToast';
import DataFreshness from '../components/DataFreshness';

/* ============================================================================
 * Card Inventory — physical stock of marketing collateral cards (cashback
 * flyers, product cards, booklets shipped inside customer orders).
 *
 * Renders inside InventoryLayout (sidebar + content shell already provided by
 * the portal) — this page is just the content: page header, stat strip, and
 * the card grid. No duplicate wordmark/topbar/avatar (the prototype this was
 * built from had its own because it had no real app shell around it).
 * ========================================================================== */

const DEBOUNCE_MS = 400;

const STATUS_META = {
  healthy: { label: 'HEALTHY',      bg: '#E8F6EF', text: '#146B48', dot: '#22A06B' },
  low:     { label: 'LOW',          bg: '#FCF2E0', text: '#8A5510', dot: '#E08A1E' },
  out:     { label: 'OUT OF STOCK', bg: '#FBEAE8', text: '#A62B22', dot: '#D14343' },
};

function computeStatus(quantity, reorderLevel, buffer) {
  if (quantity === 0) return 'out';
  if (quantity <= reorderLevel * buffer) return 'low';
  return 'healthy';
}

function todayLabel() {
  return new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

export default function CardInventory({ user }) {
  const canEdit = hasEditAccess(user, 'inventory', 'card_inventory');

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [lowStockBuffer] = useState(1); // configurable multiplier per design; not persisted

  const loadCards = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/card-inventory?action=list`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `Server returned ${res.status}`);
      setCards(Array.isArray(data) ? data : []);
      setFetchedAt(Date.now());
      setError('');
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadCards(); }, [loadCards]);

  const totals = useMemo(() => {
    const totalPieces = cards.reduce((s, c) => s + (Number(c.quantity) || 0), 0);
    const outOfStockCount = cards.filter(c => Number(c.quantity) === 0).length;
    return { totalPieces, cardTypeCount: cards.length, outOfStockCount };
  }, [cards]);

  async function persistQuantity(id, quantity) {
    try {
      const res = await fetch(`${API_BASE}/card-inventory?action=update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity, username: user?.username }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Server returned ${res.status}`);
    } catch (err) {
      notifyEditBlocked(`Couldn't save quantity: ${err.message}`);
      loadCards(); // rollback to server truth
    }
  }

  const debounceTimers = useRef({});
  function handleQuantityChange(id, rawValue) {
    const digits = rawValue.replace(/\D/g, '');
    const quantity = digits === '' ? 0 : parseInt(digits, 10);
    setCards(prev => prev.map(c => (c.id === id ? { ...c, quantity } : c)));

    clearTimeout(debounceTimers.current[id]);
    debounceTimers.current[id] = setTimeout(() => persistQuantity(id, quantity), DEBOUNCE_MS);
  }
  function handleQuantityBlur(id, currentQty) {
    clearTimeout(debounceTimers.current[id]);
    persistQuantity(id, currentQty);
  }

  async function handleAddCard({ name, quantity, photoFile }) {
    const form = new FormData();
    form.append('name', name);
    form.append('quantity', String(quantity));
    form.append('username', user?.username || '');
    if (photoFile) form.append('photo', photoFile);

    try {
      const res = await fetch(`${API_BASE}/card-inventory?action=create`, { method: 'POST', body: form });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Server returned ${res.status}`);
      setShowAddModal(false);
      notifyEditBlocked(`${data.name || name} added to inventory`);
      loadCards();
    } catch (err) {
      notifyEditBlocked(`Couldn't add card: ${err.message}`);
    }
  }

  async function handleRemoveCard(card) {
    const prev = cards;
    setCards(cs => cs.filter(c => c.id !== card.id)); // optimistic
    try {
      const res = await fetch(`${API_BASE}/card-inventory?action=archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: card.id, username: user?.username }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Server returned ${res.status}`);
      notifyEditBlocked(`${card.name} removed`);
    } catch (err) {
      setCards(prev); // rollback
      notifyEditBlocked(`Couldn't remove card: ${err.message}`);
    }
  }

  return (
    <div style={S.page}>
      <style>{EMBEDDED_CSS}</style>

      <div style={S.header}>
        <div>
          <h1 style={S.h1}>Card <span style={{ color: '#6B3FA0' }}>Inventory</span></h1>
          <div style={S.subline}>Every insert, flyer and booklet that ships in a box — counted.</div>
        </div>
        <div style={S.headerRight}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <span style={S.dateText}>{todayLabel()}</span>
            <DataFreshness fetchedAt={fetchedAt} loading={loading} onRefresh={loadCards} />
          </div>
          {canEdit && (
            <button type="button" style={S.addBtn} onClick={() => setShowAddModal(true)}
              onMouseEnter={e => { e.currentTarget.style.background = '#57318A'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#6B3FA0'; e.currentTarget.style.transform = 'none'; }}>
              <Plus size={14} /> Add new card
            </button>
          )}
        </div>
      </div>

      {error && <div style={S.errorBanner}>Couldn't load Card Inventory: {error}</div>}

      <div style={S.statStrip}>
        <div style={S.statTile}>
          <div style={{ ...S.statLabel, color: '#9C8FB5' }}>TOTAL PIECES</div>
          <div style={{ ...S.statValue, color: '#1B1626' }}>{totals.totalPieces.toLocaleString('en-IN')}</div>
        </div>
        <div style={{ ...S.statTile, background: 'linear-gradient(140deg,#6B3FA0,#4A2A73)', border: 'none', boxShadow: '0 12px 26px -14px rgba(75,42,115,.7)' }}>
          <div style={{ ...S.statLabel, color: '#D9C8F2' }}>CARD TYPES</div>
          <div style={{ ...S.statValue, color: '#fff' }}>{totals.cardTypeCount}</div>
        </div>
        <div style={S.statTile}>
          <div style={{ ...S.statLabel, color: '#9C8FB5' }}>OUT OF STOCK</div>
          <div style={{ ...S.statValue, color: '#1B1626' }}>{totals.outOfStockCount}</div>
        </div>
      </div>

      {loading && cards.length === 0 ? (
        <div style={S.grid}>
          {Array.from({ length: 9 }).map((_, i) => <SkeletonTile key={i} />)}
        </div>
      ) : cards.length === 0 ? (
        <div style={S.emptyState}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#6E6383' }}>No card types yet</div>
          {canEdit && (
            <button type="button" style={{ ...S.addBtn, marginTop: 16 }} onClick={() => setShowAddModal(true)}>
              <Plus size={14} /> Add new card
            </button>
          )}
        </div>
      ) : (
        <div style={S.grid} data-tutorial="card-inventory-table">
          {cards.map(card => (
            <CardTile
              key={card.id}
              card={card}
              status={computeStatus(Number(card.quantity) || 0, Number(card.reorder_level) || 0, lowStockBuffer)}
              canEdit={canEdit}
              onQuantityChange={v => handleQuantityChange(card.id, v)}
              onQuantityBlur={() => handleQuantityBlur(card.id, card.quantity)}
              onRemove={() => handleRemoveCard(card)}
            />
          ))}
        </div>
      )}

      {showAddModal && (
        <AddCardModal onClose={() => setShowAddModal(false)} onSubmit={handleAddCard} />
      )}
    </div>
  );
}

function SkeletonTile() {
  return (
    <div style={S.tile}>
      <div style={{ aspectRatio: '3 / 4', background: '#EFEAF7' }} />
      <div style={{ padding: '11px 12px 13px' }}>
        <div style={{ height: 12, width: '70%', background: '#EFEAF7', borderRadius: 4 }} />
        <div style={{ height: 10, width: '40%', background: '#EFEAF7', borderRadius: 4, marginTop: 10 }} />
      </div>
    </div>
  );
}

function CardTile({ card, status, canEdit, onQuantityChange, onQuantityBlur, onRemove }) {
  const st = STATUS_META[status];
  const [hover, setHover] = useState(false);

  return (
    <div
      style={{ ...S.tile, transform: hover ? 'translateY(-6px)' : 'none', boxShadow: hover ? '0 26px 46px -28px rgba(40,20,70,.55)' : S.tile.boxShadow }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div style={{ position: 'relative', aspectRatio: '3 / 4', overflow: 'hidden', background: card.tint || '#C9B6E8' }}>
        {card.photo_url ? (
          <img src={card.photo_url} alt={card.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Instrument Serif', serif", fontSize: 52, color: 'rgba(255,255,255,.92)' }}>
            {(card.name || '?').charAt(0).toUpperCase()}
          </div>
        )}
        {canEdit && (
          <button
            type="button"
            title={`Remove ${card.name}`}
            onClick={onRemove}
            style={S.removeBtn}
            onMouseEnter={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#C0392B'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,.82)'; e.currentTarget.style.color = '#6E6383'; }}
          >
            <X size={15} />
          </button>
        )}
      </div>
      <div style={{ padding: '11px 12px 13px' }}>
        <div style={S.cardName}>{card.name}</div>
        <div style={{ ...S.statusChip, background: st.bg, color: st.text }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: st.dot, flex: 'none' }} />
          {st.label}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 13, alignItems: 'center' }}>
          <input
            type="text"
            className="ci-qty-input"
            value={card.quantity ?? 0}
            disabled={!canEdit}
            onChange={e => onQuantityChange(e.target.value)}
            onBlur={onQuantityBlur}
            style={{ ...S.qtyInput, cursor: canEdit ? 'text' : 'default' }}
            onFocus={e => { e.target.style.borderColor = '#6B3FA0'; e.target.style.background = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(107,63,160,.13)'; }}
            onBlurCapture={e => { e.target.style.borderColor = '#EDE8F5'; e.target.style.background = '#FAF8FD'; e.target.style.boxShadow = 'none'; }}
          />
          <span style={S.unitLabel}>PCS</span>
        </div>
      </div>
    </div>
  );
}

function AddCardModal({ onClose, onSubmit }) {
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('1000');
  const [photoFile, setPhotoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    await onSubmit({
      name: name.trim() || 'Untitled Card',
      quantity: parseInt(quantity.replace(/\D/g, ''), 10) || 0,
      photoFile,
    });
    setSubmitting(false);
  }

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modalPanel} onClick={e => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <div style={S.modalEyebrow}>NEW COLLATERAL</div>
          <div style={S.modalTitle}>Add a card</div>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '22px 24px', display: 'grid', gap: 14 }}>
            <div>
              <div style={S.fieldLabel}>CARD NAME</div>
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Diwali Thank-You Card" style={S.textInput} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <div style={S.fieldLabel}>OPENING QTY</div>
                <input type="text" inputMode="numeric" value={quantity} onChange={e => setQuantity(e.target.value.replace(/\D/g, ''))} style={S.textInput} />
              </div>
              <div>
                <div style={S.fieldLabel}>CARD PHOTO</div>
                <label style={S.dropzone}>
                  <Upload size={13} />
                  {photoFile ? photoFile.name.slice(0, 12) : 'Upload'}
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setPhotoFile(e.target.files?.[0] || null)} />
                </label>
              </div>
            </div>
          </div>
          <div style={{ padding: '0 24px 22px', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" style={S.cancelBtn} onClick={onClose}>Cancel</button>
            <button type="submit" disabled={submitting} style={{ ...S.addCardBtn, opacity: submitting ? 0.7 : 1 }}>
              {submitting ? 'Adding…' : 'Add card'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const S = {
  page: { fontFamily: "'Plus Jakarta Sans', sans-serif" },
  header: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' },
  h1: { fontFamily: "'Instrument Serif', serif", fontSize: 44, fontWeight: 400, letterSpacing: '-.01em', color: '#1B1626', margin: 0 },
  subline: { fontSize: 14, color: '#7A6E92', marginTop: 6 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 16, paddingBottom: 8 },
  dateText: { fontSize: 13, color: '#8A7FA3' },
  addBtn: {
    display: 'flex', alignItems: 'center', gap: 6, background: '#6B3FA0', color: '#fff', border: 'none',
    fontSize: 13, fontWeight: 700, borderRadius: 999, padding: '12px 22px', cursor: 'pointer',
    boxShadow: '0 10px 22px -12px rgba(107,63,160,.95)', transition: 'background .16s ease, transform .16s ease',
  },
  errorBanner: { marginTop: 16, padding: '10px 16px', borderRadius: 10, background: '#FBEAE8', color: '#A62B22', fontSize: 13 },
  statStrip: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginTop: 30 },
  statTile: { borderRadius: 16, padding: '18px 22px', background: '#fff', border: '1px solid #E7E2F0' },
  statLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '.14em' },
  statValue: { fontFamily: "'Instrument Serif', serif", fontSize: 40, lineHeight: 1.2, marginTop: 4 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(138px, 1fr))', gap: 44, marginTop: 34 },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 0' },
  tile: {
    background: '#fff', borderRadius: 20, overflow: 'hidden',
    boxShadow: '0 2px 4px -2px rgba(40,20,70,.1), 0 14px 30px -26px rgba(40,20,70,.6)',
    transition: 'transform .2s cubic-bezier(.2,.9,.3,1.3), box-shadow .2s ease',
  },
  removeBtn: {
    position: 'absolute', top: 10, right: 10, width: 26, height: 26, borderRadius: '50%', border: 'none',
    background: 'rgba(255,255,255,.82)', backdropFilter: 'blur(4px)', color: '#6E6383', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background .15s, color .15s',
  },
  cardName: { fontSize: 13, fontWeight: 700, color: '#1B1626', lineHeight: 1.3 },
  statusChip: {
    display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 8, padding: '3px 8px 3px 7px',
    borderRadius: 999, fontSize: 9, fontWeight: 800, letterSpacing: '.1em',
  },
  qtyInput: {
    flex: 1, width: '100%', boxSizing: 'border-box', WebkitAppearance: 'none', appearance: 'none',
    height: 38, textAlign: 'center', padding: 0, fontSize: 16, fontWeight: 800, color: '#1B1626',
    background: '#FAF8FD', border: '1.5px solid #EDE8F5', borderRadius: 11, outline: 'none',
    transition: 'border-color .16s ease, box-shadow .16s ease, background .16s ease',
  },
  unitLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: '#B3A6C7' },

  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(27,22,38,.42)', backdropFilter: 'blur(3px)', zIndex: 60,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
  },
  modalPanel: { width: 480, maxWidth: '100%', background: '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: '0 40px 90px -40px rgba(20,10,40,.7)' },
  modalHeader: { background: 'linear-gradient(140deg,#6B3FA0,#4A2A73)', padding: '20px 24px' },
  modalEyebrow: { fontSize: 11, fontWeight: 700, letterSpacing: '.16em', color: '#D9C8F2' },
  modalTitle: { fontFamily: "'Instrument Serif', serif", fontSize: 28, color: '#fff', marginTop: 2 },
  fieldLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '.12em', color: '#9C8FB5', marginBottom: 6 },
  textInput: { width: '100%', border: '1.5px solid #E7E2F0', borderRadius: 10, padding: '11px 13px', fontSize: 14, color: '#1B1626', outline: 'none', boxSizing: 'border-box' },
  dropzone: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, border: '1.5px dashed #D3C4EB',
    borderRadius: 10, padding: '11px 13px', fontSize: 13, fontWeight: 600, color: '#6B3FA0', background: '#FBF9FE', cursor: 'pointer',
  },
  cancelBtn: { background: '#fff', border: '1px solid #E7E2F0', color: '#6E6383', borderRadius: 10, padding: '11px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  addCardBtn: { background: '#6B3FA0', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 22px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
};

const EMBEDDED_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.ci-qty-input { text-align: center !important; }
`;
